import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Article } from '@/module/Author/Article';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { CriteriaArticleSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaArticleSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportArticleSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportArticleSelect({ labId, criteriaCode }: CenterReportArticleSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      articles: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (article: Article) => onSubmit({ toAdd: article });
  const onRemove = (article: Article) => onSubmit({ toRemove: article });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaArticleSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((article, index) => (
                <Flex
                  align="center"
                  key={article.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(article)}
                    />
                  )}
                  <ArticleCard
                    article={article}
                    onClick={() => window.open(`/author/article/${article.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
